import { emptyBackWeight } from '@/library/BackWeightHelper';
import { useState, useMemo, Fragment } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import EditableTile from '@/common/EditableTile';
import EditableDropdown, { IDropdownOption } from '@/common/EditableDropdown';
import Tile from '@/common/Tile';
import Toggle from '@/common/Toggle';
import RadioGroup from '@/common/RadioGroup';
import AddIronsModal from './AddIronsModal';
import FinalHeadWeightModal, { IFinalHeadWeightIron } from './FinalHeadWeightModal';
import { TourBagIronSet, TourBagIronDetail } from '@/api/models';
import { createEmptyTourBagIronDetail, normalizeString } from '@/utils';
import { buildSetIronPayload, buildIronMetadata } from './helpers';
import { calculateFinalHeadWeight } from './caluclateFinalHeadWeight';
import { IUseEditIronResult } from './useEditIron';
import clubStyles from '../Club.module.scss';
import setStyles from '../Set.module.scss';
import { handAbbreviationToFullName, handFullNameToAbbreviation } from '@/library/HandHelper';

export interface IIronSet {
  tourBagIronSetId: number;
  setName: string;
  setHand: string;
  setSwingWt: string;
  setShaftId: number;
  setShaftMeasured: boolean;
  setShaftCosmetic: string;
  setGripType: string;
  setGripSizeId: number;
  setRoundRib: 'Round' | 'Rib';
  setLogo: 'Up' | 'Down';
  setGripTape: string;
  setNotes: string;
  irons: TourBagIronDetail[];
}

interface IEditIronProps extends IUseEditIronResult {
  title: 'Create Set' | 'Edit Set';
  action: 'create' | 'edit';
  ironSet: TourBagIronSet;
  tourBagMstrId: number | undefined;
  setNames: string[];
  // eslint-disable-next-line no-unused-vars
  handleSave: (ironSet: TourBagIronSet) => Promise<void>;
  handleCancel: () => void;
}

function EditSet({
  title,
  action,
  ironSet,
  tourBagMstrId,
  setNames,
  ironClubData,
  handDropdownData,
  loftDropdownData,
  lieDropdownData,
  ironStaticDropdownData,
  ironDynamicDropdownData,
  shaftDropdownData,
  shaftLengthDropdownData,
  swingWtDropdownData,
  roundRibDropdownData,
  logoDropdownData,
  handleSave,
  handleCancel,
}: IEditIronProps) {
  const { player } = useActivePlayer();
  const [errors, setErrors] = useState('');
  const [showAddIronsModal, setShowAddIronsModal] = useState(false);
  const [showFinalHWModal, setShowFinalHWModal] = useState(false);
  const [finalHWIron, setFinalHWIron] = useState<TourBagIronDetail | null>(null);
  const [finalHWIronIdx, setFinalHWIronIdx] = useState<number | null>(null);
  // set field state
  const [setName, setSetName] = useState(ironSet.tourBagIronSetName);
  const [setHand, setSetHand] = useState(handAbbreviationToFullName(ironSet.hand));
  const initialShaftId = useMemo(() => shaftDropdownData?.options.find((ironShaft) => ironShaft.label.endsWith(ironSet.shaftName))?.id ?? '', [ironSet.shaftName, shaftDropdownData?.options]);
  const [setShaftId, setSetShaftId] = useState(ironSet.shaftName ? initialShaftId : '');
  const [setShaftMeasured, setSetShaftMeasured] = useState(ironSet.isShaftLengthFinished ? true : false);
  const [setShaftCosmetic, setSetShaftCosmetic] = useState(ironSet.shaftCosmetic);
  const [setSwingWt, setSetSwingWt] = useState(`${ironSet.swingWtPrefixName}${ironSet.swingWtSuffixName}`);
  const [setGripType, setSetGripType] = useState(ironSet.gripType);
  const [setGripSizeId, setSetGripSizeId] = useState(ironSet.gripSizeId || '');
  const [setRoundRib, setSetRoundRib] = useState(ironSet.rndRib);
  const [setLogo, setSetLogo] = useState(ironSet.logo);
  const [setGripTape, setSetGripTape] = useState(ironSet.tape);
  const [setNotes, setSetNotes] = useState(ironSet.notes);

  // set iron state
  // For set clubs we use the same interface/ contract from the API to avoid tracking individual props
  const [ironClubs, setIronClubs] = useState<TourBagIronDetail[]>([...ironSet.tourBagIronDetails].sort((a, b) => a.sortId! - b.sortId!));

  const handleUpdateFinalHW = (partialIron: IFinalHeadWeightIron) => {
    const updatedIronClubs = [...ironClubs].map((iron, index) => {
      if (index === finalHWIronIdx) {
        return { ...iron, ...partialIron };
      }
      return iron;
    });
    // update local state
    setIronClubs(updatedIronClubs);
  };

  const getHeadModelOptions = (familyId: number | undefined): IDropdownOption[] => {
    if (!ironDynamicDropdownData?.ironHeadModel) return [];
    return ironDynamicDropdownData.ironHeadModel.getOptions({ familyId });
  };

  const updateIronClub = (index: number, key: keyof TourBagIronDetail, value: any) => {
    setIronClubs((prev) => {
      const newClubs = [...prev];
      newClubs[index] = { ...newClubs[index], ...(key === 'headModelId' ? emptyBackWeight : {}), [key]: value };
      return newClubs;
    });
  };

  const serializeIronShafts = (setShaftName: string) => {
    const baseName = setShaftName.replace(/\s#\d+$/, '').trim();
    const shaftGroup = shaftDropdownData?.groups[baseName];
    if (!shaftGroup || shaftGroup.length === 0) return;

    // Find the index of the selected shaft
    const startIndex = shaftGroup.findIndex((s) => s.label.trim() === setShaftName.trim());
    const initialIndex = startIndex >= 0 ? startIndex : 0;

    const updatedClubs = ironClubs.map((iron, index) => {
      const shaftIndex = Math.min(initialIndex + index, shaftGroup.length - 1);
      const shaftLabel = shaftGroup[shaftIndex].label;

      return {
        ...iron,
        shaftName: shaftLabel,
      };
    });

    setIronClubs(updatedClubs);
  };

  const cancelEdition = () => {
    // reset values
    setErrors('');
    setSetName('');
    setSetHand('');
    setSetShaftId('');
    setSetShaftMeasured(false);
    setSetShaftCosmetic('');
    setSetSwingWt('');
    setSetGripType('');
    setSetGripSizeId('');
    setSetRoundRib('');
    setSetLogo('');
    setSetGripTape('');
    setSetNotes('');
    setIronClubs([]);
    // exit
    handleCancel();
  };

  const saveSet = async () => {
    const isCreateMode = action === 'create'; // For creation we set set and iron primary keys to 0
    // If the set name was changed we check it doesn't already exist
    const isDuplicateSetName = setNames.includes(setName?.toLocaleLowerCase());
    const isValidIronPayload = ironClubs.every((club) => club.familyId && club.headModelId);
    // when editing the set name can be the same but if changed it can't be a duplicate
    const isInvalidEditionSetName = setName.toLowerCase() !== ironSet.tourBagIronSetName.toLowerCase() && isDuplicateSetName;
    if (!setName) {
      setErrors('Please pick a set name.');
      return;
    }
    if ((isCreateMode && isDuplicateSetName) || (!isCreateMode && isInvalidEditionSetName)) {
      setErrors('The set name already exists, please pick a different one.');
      return;
    }
    if (!isValidIronPayload) {
      setErrors('Please select a family and a head model for each iron in the set.');
      return;
    }
    setErrors(''); // reset errors
    const swingWtData = swingWtDropdownData?.map[setSwingWt];
    const shaftData = shaftDropdownData?.map[setShaftId];
    const gripData = ironStaticDropdownData?.gripSize.map[setGripSizeId];
    // Build the set data with the shared props
    const setData: TourBagIronSet = {
      ...ironSet,
      ...(isCreateMode ? { rowGuid: '' } : {}),
      // Set id to 0 to create a new set
      tourBagMstrId: tourBagMstrId || 0,
      tourBagIronSetId: isCreateMode ? 0 : ironSet.tourBagIronSetId || 0, // TODO: API Requires 0 for tourBagIronSetId, update backend to accomodate -1 as the rest
      tourBagIronSetName: setName,
      hand: handFullNameToAbbreviation(setHand),
      shaftName: shaftData?.name || '',
      isShaftLengthFinished: setShaftMeasured,
      shaftCosmetic: setShaftCosmetic,

      // Swing Weight
      swingWtPrefixId: swingWtData?.swingWtPrefixId || -1,
      swingWtPrefixName: swingWtData?.swingWtPrefixName || '',
      swingWtSuffixId: swingWtData?.swingWtSuffixId || -1,
      swingWtSuffixName: swingWtData?.swingWtSuffixName || '',

      // Grip
      gripType: setGripType,
      gripSizeId: Number(setGripSizeId),
      gripSize: gripData?.name || '',
      tape: setGripTape,
      rndRib: setRoundRib,
      logo: setLogo,

      // Notes
      notes: setNotes,
    };
    // With the set data build each iron of the set
    const setIronsData: TourBagIronDetail[] = ironClubs.map((club) => {
      const ironPayload = {
        ...createEmptyTourBagIronDetail({ ironClubId: club.ironClubId, ironClubName: club.ironClubName }),
        ...buildSetIronPayload({
          iron: club,
          set: setData,
          action,
          tourBagMstrId,
          ironStaticDropdownData,
          ironDynamicDropdownData,
          loftDropdownData,
          lieDropdownData,
          shaftLengthDropdownData,
          swingWtDropdownData,
        }),
        ...buildIronMetadata({ player }),
      };

      if (isCreateMode) {
        ironPayload.rowGuid = '';
      }

      return ironPayload;
    });
    const setPayload = {
      ...setData,
      tourBagIronDetails: setIronsData, // array of set irons
    };
    await handleSave(setPayload);
  };

  if (
    !ironClubData ||
    !handDropdownData ||
    !loftDropdownData ||
    !lieDropdownData ||
    !ironStaticDropdownData ||
    !ironDynamicDropdownData ||
    !shaftDropdownData ||
    !shaftLengthDropdownData ||
    !swingWtDropdownData
  )
    return null;

  return (
    <>
      {/* Set Properties */}
      <div className={`${clubStyles.tileRow} ${clubStyles.title}`} data-testid="tourbag-iron-edit-form-root">
        {title}
      </div>
      <div className={clubStyles.category}>
        <div className={clubStyles.categoryName}>Set Properties</div>
      </div>
      <div className={clubStyles.tileRow}>
        <EditableTile label="Set Name" value={setName} onChange={(setName) => setSetName(setName)} variant="large" />
        <EditableDropdown label="Hand" initialId={setHand} onChange={(hand) => setSetHand(hand)} options={handDropdownData.options} />
      </div>
      {/* Render irons inside the set */}
      {ironClubs.map((iron, index) => {
        const headModelOptions = iron.familyId ? getHeadModelOptions(iron.familyId) : [];
        const setClubsCount = ironSet.tourBagIronDetails.length;
        const initialShaftId = (iron.shaftName && shaftDropdownData.options.find((ironShaft) => ironShaft.label.endsWith(iron.shaftName))?.id) ?? '';
        const finalHeadWeight = calculateFinalHeadWeight({
          headWeight: iron.headWeight,
          drill: iron.drill,
          tipPlug: iron.tipPlug,
          glueWeight: iron.glueWeight,
          leadTape: iron.leadTape,
        });

        return (
          <Fragment key={iron.tourBagIronDetailId || uuidv4()}>
            <div className={setStyles.setDivisionLine} />
            <div className={`${clubStyles.tileRow} ${setStyles.setRow}`} data-testid="tourbag-iron-set-club-row">
              <div className={setStyles.editSetClub}>
                <span>{iron.ironClubName}</span>
              </div>
              <EditableDropdown
                label="Family"
                initialId={iron.familyId || ''}
                onChange={(familyId) => {
                  updateIronClub(index, 'familyId', Number(familyId));
                  updateIronClub(index, 'headModelId', undefined);
                }}
                options={ironStaticDropdownData.ironFamily.options}
                variant="large"
              />
              <EditableDropdown
                label="Model"
                initialId={iron.headModelId || ''}
                onChange={(headModelId) => updateIronClub(index, 'headModelId', Number(headModelId))}
                options={headModelOptions}
                variant="large"
              />
              <EditableDropdown
                label="Loft"
                initialId={`${iron.loftDigit}${iron.loftFraction}`}
                onChange={(loft) => {
                  const loftData = loftDropdownData.map[loft];
                  updateIronClub(index, 'loftDigit', loftData.loftDigit || '');
                  updateIronClub(index, 'loftFraction', loftData.loftFraction || '');
                }}
                options={loftDropdownData.options}
              />
              <EditableDropdown
                label="Lie"
                initialId={`${iron.lieDigit}${iron.lieFraction}`}
                onChange={(lie) => {
                  const lieData = lieDropdownData.map[lie];
                  updateIronClub(index, 'lieDigit', lieData.lieDigit || '');
                  updateIronClub(index, 'lieFraction', lieData.lieFraction || '');
                }}
                options={lieDropdownData.options}
              />
              <EditableDropdown
                label="Length"
                initialId={`${iron.shaftLengthDigit} ${iron.shaftLengthFraction}`}
                onChange={(shaftLength) => {
                  const shaftLengthData = shaftLengthDropdownData.map[shaftLength];
                  updateIronClub(index, 'shaftLengthDigit', shaftLengthData.digit || '');
                  updateIronClub(index, 'shaftLengthFraction', shaftLengthData.fraction || '');
                }}
                options={shaftLengthDropdownData.options}
              />
              <span
                className={clubStyles.finalHWWrapper}
                role="button"
                onClick={() => {
                  setFinalHWIron(iron);
                  setFinalHWIronIdx(index);
                  setShowFinalHWModal(true);
                }}
              >
                <Tile label="Final HW" value={normalizeString(finalHeadWeight?.toString())} tileStyle={clubStyles.finalHW} />
              </span>
              <Toggle isActive={iron.isActive} label={iron.isActive ? 'In the Bag' : 'Out of Bag'} onChange={() => updateIronClub(index, 'isActive', !iron.isActive)} />
            </div>
            {/* Iron-level Shaft */}
            <div className={clubStyles.tileRow}>
              <div className={clubStyles.categoryName}>Shaft</div>
            </div>
            <div className={clubStyles.tileRow}>
              <EditableDropdown
                key={iron.shaftName} // make React re-render component when serializing iron shaft
                label="Shaft"
                initialId={initialShaftId || ''}
                onChange={(shaftId) => {
                  const shaftName = shaftDropdownData.map[shaftId].name;
                  updateIronClub(index, 'shaftName', shaftName || '');
                }}
                options={shaftDropdownData.options}
                type="rectangle"
                alignment="left"
                variant="xxxxl"
              />
            </div>
            {index === setClubsCount - 1 && <div className={setStyles.setDivisionLine} />}
          </Fragment>
        );
      })}
      {!ironSet.tourBagIronDetails.length && <div className={setStyles.setDivisionLine} />}
      <div className={clubStyles.tileRow}>
        <button
          type="button"
          className={`${clubStyles.categoryName} ${setStyles.addClub} ${setStyles.addClubButton}`}
          data-testid="tourbag-iron-add-remove-club-button"
          aria-label="Add or remove iron clubs"
          onClick={() => setShowAddIronsModal(true)}
        >
          Add/Remove Club
        </button>
      </div>
      <div className={setStyles.setDivisionLine} />
      {/* Set-level Shaft */}
      <div className={clubStyles.tileRow}>
        <div className={clubStyles.categoryName}>Shaft</div>
      </div>
      <div className={clubStyles.tileRow}>
        <EditableDropdown
          label="Shaft"
          initialId={setShaftId || ''}
          onChange={(shaftId) => {
            setSetShaftId(shaftId);
            const shaftName = shaftDropdownData.map[shaftId].name;
            serializeIronShafts(shaftName);
          }}
          options={shaftDropdownData.options}
          type="rectangle"
          alignment="left"
          variant="xxxxl"
        />
        {/* setting !measured to render the second options since its reversed in the designs */}
        <div className={clubStyles.radioGroup}>
          <RadioGroup selectedOption={!setShaftMeasured} firstLabel="Cut" secondLabel="Finished" onChange={() => setSetShaftMeasured((prevState) => !prevState)} />
        </div>
      </div>
      <div className={`${clubStyles.tileRow} ${clubStyles.rowSection}`}>
        <EditableTile label="Cosmetic" value={setShaftCosmetic} onChange={(shaftCosmetic) => setSetShaftCosmetic(shaftCosmetic)} />
        <EditableDropdown label="Swing Wt" initialId={setSwingWt} onChange={(swingWt) => setSetSwingWt(swingWt)} options={swingWtDropdownData.options} />
      </div>
      {/* Set-level Grip */}
      <div className={clubStyles.tileRow}>
        <div className={clubStyles.categoryName}>Set Grip</div>
      </div>
      <div className={`${clubStyles.tileRow} ${clubStyles.rowSection}`}>
        <EditableTile value={setGripType} onChange={(gripType) => setSetGripType(gripType)} type="rectangle" alignment="left" variant="xxl" />
      </div>
      <div className={`${clubStyles.tileRow} ${clubStyles.rowSection}`}>
        <EditableDropdown label="Grip Size" initialId={setGripSizeId} onChange={(gripSizeId) => setSetGripSizeId(Number(gripSizeId))} options={ironStaticDropdownData.gripSize.options} />
        <EditableDropdown label="Round/Rib" initialId={setRoundRib} options={roundRibDropdownData} onChange={(roundRib) => setSetRoundRib(roundRib)} />
        <EditableDropdown label="Logo" initialId={setLogo} options={logoDropdownData} onChange={(logo) => setSetLogo(logo)} />
        <EditableTile label="Grip Tape" value={setGripTape} onChange={(gripTape) => setSetGripTape(gripTape)} variant="large" />
      </div>
      {/* Set-level Notes */}
      <div className={clubStyles.tileRow}>
        <div className={clubStyles.categoryName}>Notes</div>
      </div>
      <div className={clubStyles.tileRow}>
        <textarea className={clubStyles.editNotes} value={setNotes} name="notes" onChange={(event) => setSetNotes(event.target.value)} rows={5} />
      </div>
      {showAddIronsModal && (
        <AddIronsModal ironClubsData={ironClubData} ironClubs={ironClubs} setIronClubs={setIronClubs} showAddIronsModal={showAddIronsModal} closeAddIronsModal={() => setShowAddIronsModal(false)} />
      )}
      {showFinalHWModal && finalHWIron && (
        <FinalHeadWeightModal
          showFinalHWModal={showFinalHWModal}
          closeFinalHWModal={() => {
            setFinalHWIron(null);
            setFinalHWIronIdx(null);
            setShowFinalHWModal(false);
          }}
          iron={finalHWIron}
          backWeightData={ironStaticDropdownData.backWeight}
          handleUpdate={handleUpdateFinalHW}
          isEditMode={true}
        />
      )}
      {errors && <span className={clubStyles.errors}>{errors}</span>}
      <div className={clubStyles.buttonRow}>
        <button className={clubStyles.saveButton} onClick={saveSet}>
          Save
        </button>
        <button className={clubStyles.cancelButton} onClick={cancelEdition}>
          Cancel
        </button>
      </div>
    </>
  );
}

export default EditSet;
